"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, Link, Check } from "lucide-react";
import { useConfigurator } from "@/store/configurator";
import { formatPrice } from "@/lib/utils";
import { formatGauge, isWound } from "@/lib/catalog/gauge-format";
import { resolveSelectionLabels } from "@/lib/configurator/selection-labels";
import { StringPreview } from "@/components/configurator/StringPreview";
import { chipStyle } from "@/components/configurator/chip-style";
import { isSafeCheckoutUrl } from "@/lib/cart/view-model";

const WINDING_LABELS: Record<string, string> = {
  plain:      "Plain Steel",
  round_wound: "Round Wound",
  flat_wound:  "Flat Wound",
  half_wound:  "Half Wound",
};

interface QuantityTier {
  minQuantity: number;
  multiplier: number;
  label: string | null;
}

interface PricingState {
  unitPrice: number;
  totalPrice: number;
  discountPct: number;
  quantityTiers: QuantityTier[];
}

interface Props {
  onNext: () => void;
  onBack: () => void;
  /** Jump straight to the step that owns a given choice. */
  onEditStep: (step: number) => void;
}

export function ReviewStep({ onBack, onEditStep }: Props) {
  const { state, referenceData, setSetName, setQuantity, setPrices } = useConfigurator();

  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [pricing, setPricing] = useState<PricingState>({
    unitPrice: state.unitPrice ?? 0,
    totalPrice: state.totalPrice ?? 0,
    discountPct: 0,
    quantityTiers: [{ minQuantity: 1, multiplier: 1, label: "1 set" }],
  });
  const [priceStatus, setPriceStatus] = useState<"loading" | "ready" | "error">("loading");
  const [priceRetry, setPriceRetry] = useState(0);
  const [quoteId, setQuoteId] = useState<string | null>(null);

  // Selections are slugs — see the contract in hooks/useConfiguratorUrlSync.ts.
  const labels = resolveSelectionLabels(state, referenceData);
  const colorMap = useMemo(
    () =>
      new Map(
        referenceData.lines
          .flatMap((entry) => entry.colors)
          .map((color) => [color.slug, color])
      ),
    [referenceData.lines]
  );
  const setColor = labels.color;
  // Compact confirmation strip: the same per-string colours and gauges the
  // full board showed, at a size that does not push the order card off screen.
  const previewStrings = state.strings.map((str) => {
    const c = colorMap.get(str.colorId ?? state.colorId ?? "") ?? setColor;
    return {
      position: str.position,
      colorHex: c.hexCode ?? "#3A3A3A",
      // Store format is 0.0100; the strip shows the .010 the customer reads.
      gaugeValue: `${formatGauge(str.gaugeValue)}${isWound(str.windingType) ? "w" : ""}`,
    };
  });
  const quoteRequest = JSON.stringify({
    instrumentId: state.instrumentId, lineId: state.lineId, stringTypeId: state.stringTypeId,
    stringCount: state.stringCount, scaleLengthId: state.scaleLengthId, colorId: state.colorId,
    presetId: state.presetId, strings: state.strings, quantity: state.quantity,
  });

  useEffect(() => {
    const controller = new AbortController();
    async function loadPricing() {
      setPriceStatus("loading");
      setQuoteId(null);

      try {
        const response = await fetch("/api/configurator/price", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: quoteRequest,
        });

        const data = await response.json().catch(() => null) as {
          error?: unknown;
          quoteId?: unknown;
          unitPrice?: unknown;
          totalPrice?: unknown;
          discountPct?: unknown;
          quantityTiers?: unknown;
        } | null;
        if (controller.signal.aborted) return;
        if (!response.ok) {
          throw new Error(typeof data?.error === "string" ? data.error : "Pricing unavailable");
        }
        if (typeof data?.quoteId !== "string" || !/^[a-f0-9]{64}$/.test(data.quoteId) ||
            typeof data.unitPrice !== "number" || !Number.isFinite(data.unitPrice) || data.unitPrice <= 0 ||
            typeof data.totalPrice !== "number" || !Number.isFinite(data.totalPrice) || data.totalPrice <= 0 ||
            typeof data.discountPct !== "number" || !Number.isFinite(data.discountPct) || !Array.isArray(data.quantityTiers)) {
          throw new Error("Pricing unavailable");
        }

        setPricing({
          unitPrice: data.unitPrice,
          totalPrice: data.totalPrice,
          discountPct: data.discountPct,
          quantityTiers: data.quantityTiers as QuantityTier[],
        });
        setPrices(data.unitPrice, data.totalPrice);
        setQuoteId(data.quoteId);
        setPriceStatus("ready");
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Pricing unavailable");
        setPriceStatus("error");
      }
    }

    void loadPricing();
    return () => controller.abort();
  }, [
    setPrices,
    priceRetry,
    quoteRequest,
  ]);

  async function handleSubmit() {
    if (!agreed || submitting || priceStatus !== "ready" || !quoteId) return;
    setSubmitting(true);
    setError(null);

    try {
      // 1. Save configuration to DB
      const res = await fetch("/api/configurator/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...state,
          quoteId,
          unitPrice: pricing.unitPrice,
          totalPrice: pricing.totalPrice,
        }),
      });

      const data = await res.json().catch(() => null) as { error?: unknown; checkoutUrl?: unknown } | null;
      if (!res.ok) {
        throw new Error(typeof data?.error === "string" ? data.error : "Configuration checkout failed. Please retry");
      }
      if (typeof data?.checkoutUrl !== "string" || !isSafeCheckoutUrl(data.checkoutUrl)) {
        throw new Error("Configuration checkout failed. Please retry");
      }

      // The server has already validated the URL and persisted the draft order
      // identity. Never navigate to an arbitrary response value or show a
      // local "dev mode" success that did not create a checkout.
      window.location.href = data.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSubmitting(false);
    }
  }

  const unit = pricing.unitPrice;
  const total = pricing.totalPrice;
  const discountPct = pricing.discountPct;

  return (
    <div>
      {/* Review-specific layout. The editing steps put a full fretboard above
          their controls because the board IS the control; here it is only a
          confirmation, so the board becomes a compact strip and the order card
          takes the top of the column. At 1440x900 the summary, the
          authoritative price and the purchase action are all reachable without
          scrolling past an illustration. */}
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-2xl font-black" style={{ fontFamily: "var(--font-display)" }}>
          Review your set.
        </h2>
        <p className="text-sm" style={{ color: "#9A9A9A" }}>
          Confirm everything looks right before ordering.
        </p>
      </div>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-6">
        {/* ─── Order column ─────────────────────────────────────────────── */}
        <aside className="mb-6 lg:order-2 lg:sticky lg:top-6 lg:mb-0">
          <div
            className="rounded-md border px-5 py-4 space-y-4"
            style={{ backgroundColor: "#141414", borderColor: "#2A2A2A" }}
          >
            <div>
              <p className="text-sm font-semibold mb-3">Quantity</p>
              <div className="flex flex-wrap gap-2">
                {pricing.quantityTiers.map((tier) => (
                  <button
                    key={tier.minQuantity}
                    type="button"
                    onClick={() => setQuantity(tier.minQuantity)}
                    aria-pressed={state.quantity === tier.minQuantity}
                    className="px-4 py-2.5 text-sm font-medium rounded-sm border transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                    style={chipStyle(state.quantity === tier.minQuantity)}
                  >
                    {tier.label ?? `${tier.minQuantity} sets`}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t pt-4 space-y-2" style={{ borderColor: "#2A2A2A" }}>
              {priceStatus === "error" ? (
                <div
                  className="rounded-sm border px-4 py-3 space-y-3"
                  style={{ borderColor: "#E8404066", backgroundColor: "#1A0D0D" }}
                >
                  <p className="text-sm" style={{ color: "#F5B5B5" }}>
                    {error ?? "We couldn't load pricing for this set."}
                  </p>
                  <button
                    type="button"
                    onClick={() => setPriceRetry((n) => n + 1)}
                    className="px-4 py-2 text-sm font-semibold rounded-sm border transition-colors hover:border-[#E8C547] hover:text-[#E8C547] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                    style={{ borderColor: "#3A3A3A", color: "#F5F5F5" }}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <Row label="Unit price" value={priceStatus === "ready" ? formatPrice(unit) : "…"} />
                  {priceStatus === "ready" && discountPct > 0 && (
                    <Row label={`Quantity discount (−${discountPct}%)`} value={`−${formatPrice((unit * state.quantity) - total)}`} accent />
                  )}
                  <Row label="Quantity" value={`× ${state.quantity}`} />
                  <div className="border-t pt-2 flex items-center justify-between gap-3" style={{ borderColor: "#2A2A2A" }}>
                    <span className="font-bold">Order Total</span>
                    <span className="text-xl font-black" style={{ color: "#E8C547" }}>
                      {priceStatus === "ready" ? formatPrice(total) : "…"}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: "#8A8A8A" }}>
                    Shipping calculated at checkout.
                  </p>
                </>
              )}
            </div>

            <div className="border-t pt-4" style={{ borderColor: "#2A2A2A" }}>
              <label className="flex items-start gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded accent-[#E8C547] shrink-0"
                />
                <span className="text-sm" style={{ color: "#F5F5F5" }}>
                  I understand production, dispatch, delivery and custom return terms require confirmation.
                </span>
              </label>

              {error && priceStatus !== "error" && (
                <p className="mb-3 text-sm" style={{ color: "#E84040" }}>{error}</p>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!agreed || submitting || priceStatus !== "ready"}
                className="w-full rounded-sm bg-[#E8C547] py-3 text-sm font-bold text-[#0A0A0A] transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:bg-[#5C522F] disabled:text-[#F5F5F5]"
              >
                {submitting
                  ? "Redirecting to Checkout…"
                  : priceStatus === "ready"
                    ? `Continue to Checkout · ${formatPrice(total)}`
                    : "Continue to Checkout"}
              </button>

              <div className="mt-3 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onBack}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold border rounded-sm transition-colors hover:border-[#E8C547] hover:text-[#E8C547] disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                  style={{ backgroundColor: "#141414", borderColor: "#3A3A3A", color: "#F5F5F5" }}
                >
                  <ArrowLeft size={15} aria-hidden />
                  Back
                </button>
                <CopyLinkButton />
              </div>
            </div>
          </div>
        </aside>

        {/* ─── Specification column ─────────────────────────────────────── */}
        <div className="lg:order-1 space-y-5">
          {/* Compact confirmation of the built set. The full interactive board
              stays on the gauge and colour steps, where it is the control. */}
          {previewStrings.length > 0 && (
            <div
              className="rounded-md border px-4 py-3 max-w-md"
              style={{ backgroundColor: "#141414", borderColor: "#2A2A2A" }}
            >
              <StringPreview strings={previewStrings} height={96} showLabels interactive={false} />
            </div>
          )}

          <div className="rounded-md border overflow-hidden" style={{ borderColor: "#2A2A2A" }}>
            <div
              className="px-5 py-3 border-b flex items-center justify-between gap-3"
              style={{ backgroundColor: "#1E1E1E", borderColor: "#2A2A2A" }}
            >
              <button
                type="button"
                onClick={() => setSummaryOpen((open) => !open)}
                aria-expanded={summaryOpen}
                className="flex flex-1 items-center justify-between gap-3 text-left text-xs font-semibold uppercase tracking-widest focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547] lg:pointer-events-none"
                style={{ color: "#8A8A8A" }}
              >
                <span>Configuration Summary</span>
                <ChevronDown
                  size={14}
                  aria-hidden
                  className="transition-transform lg:hidden"
                  style={{ transform: summaryOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                />
              </button>
            </div>

            <div
              className={summaryOpen ? "px-5 py-4 space-y-3" : "hidden lg:block lg:px-5 lg:py-4 lg:space-y-3"}
              style={{ backgroundColor: "#141414" }}
            >
              <Row label="Instrument"    value={labels.instrumentAndCount} onEdit={() => onEditStep(1)} editLabel="Change instrument" />
              <Row label="Line"          value={labels.line} onEdit={() => onEditStep(3)} editLabel="Change line" />
              {labels.stringType ? (
                <Row label="String Type" value={labels.stringType} onEdit={() => onEditStep(3)} editLabel="Change string type" />
              ) : null}
              <Row
                label="Set Color"
                value={setColor.name}
                color={setColor.hexCode ?? "#3A3A3A"}
                onEdit={() => onEditStep(5)}
                editLabel="Change colour"
              />
              {state.isPerStringColor && (
                <Row label="Color Mode" value="Per-string mix" accent />
              )}
              {state.isCustomGauge && (
                <Row label="Build" value="Custom (modified gauges)" accent />
              )}

              <div className="border-t pt-3 mt-3" style={{ borderColor: "#2A2A2A" }}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#8A8A8A" }}>
                    Per-String Configuration
                  </p>
                  <EditLink onClick={() => onEditStep(4)} label="Change gauges" />
                </div>
                {state.strings.map((str) => {
                  const c = colorMap.get(str.colorId ?? state.colorId ?? "") ?? setColor;
                  return (
                    <div
                      key={str.position}
                      className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1 text-sm"
                    >
                      <span className="w-5 text-xs font-mono" style={{ color: "#8A8A8A" }}>
                        {str.position}
                      </span>
                      <span className="w-12 font-mono text-xs" style={{ color: "#9A9A9A" }}>
                        {formatGauge(str.gaugeValue)}{isWound(str.windingType) ? "w" : ""}
                      </span>
                      <span className="flex-1 min-w-[7rem] text-xs" style={{ color: "#9A9A9A" }}>
                        {WINDING_LABELS[str.windingType] ?? str.windingType}
                      </span>
                      <span
                        className="w-3 h-3 rounded-full border shrink-0"
                        style={{ background: c.swatchCss ?? c.hexCode ?? "#3A3A3A", borderColor: "#3A3A3A" }}
                      />
                      <span className="text-xs" style={{ color: "#8A8A8A" }}>
                        {c.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Secondary: naming the set is optional and never blocks ordering. */}
          <div>
            <label htmlFor="set-name" className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "#8A8A8A" }}>
              Name your set{" "}
              <span className="font-normal normal-case tracking-normal">
                (optional — appears on your order, no account needed)
              </span>
            </label>
            <input
              id="set-name"
              type="text"
              value={state.setName}
              onChange={(e) => setSetName(e.target.value.slice(0, 24))}
              placeholder="e.g. Midnight Blues"
              className="w-full px-4 py-2.5 text-sm border rounded-sm outline-none transition-colors focus:border-[#E8C547]"
              style={{ backgroundColor: "#141414", borderColor: "#2A2A2A", color: "#F5F5F5" }}
            />
          </div>

          <div
            className="rounded-md border px-4 py-3 text-sm"
            style={{ backgroundColor: "#141414", borderColor: "#2A2A2A", color: "#B0B0B0" }}
          >
            <p className="font-bold mb-1" style={{ color: "#F5F5F5" }}>
              Built just for you.
            </p>
            <p>
              Production, dispatch, delivery and custom return terms require confirmation.
            </p>
            <p className="mt-2">
              <a href="/contact">Contact us before purchasing.</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyLinkButton() {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleCopy() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-2 px-4 py-3 text-sm font-semibold border rounded-sm transition-all hover:border-[#3A3A3A]"
      style={{ backgroundColor: "#141414", borderColor: "#3A3A3A", color: copied ? "#40C070" : "#F5F5F5" }}
      title="Copy shareable link to this build"
    >
      {copied ? <Check size={14} /> : <Link size={14} />}
      {copied ? "Copied!" : "Copy link"}
    </button>
  );
}

/** Quiet, keyboard-reachable way back to the step that owns a choice. */
function EditLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="shrink-0 text-xs underline underline-offset-2 transition-colors hover:text-[#E8C547] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
      style={{ color: "#9A9A9A" }}
    >
      Edit
    </button>
  );
}

function Row({
  label,
  value,
  color,
  accent,
  onEdit,
  editLabel,
}: {
  label: string;
  value: string;
  color?: string;
  accent?: boolean;
  onEdit?: () => void;
  editLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="shrink-0" style={{ color: "#8A8A8A" }}>{label}</span>
      <div className="flex min-w-0 items-center gap-2">
        {color && (
          <span
            className="w-3 h-3 rounded-full border shrink-0"
            style={{ backgroundColor: color, borderColor: "#3A3A3A" }}
          />
        )}
        <span className="truncate" style={{ color: accent ? "#E8C547" : "#F5F5F5" }}>{value}</span>
        {onEdit && editLabel ? <EditLink onClick={onEdit} label={editLabel} /> : null}
      </div>
    </div>
  );
}
